import apiClient from "./api";

/**
 * Customer Service API Service
 * Handles all API calls related to customer service assignments
 */
class CustomerServiceService {
  /**
   * Handle axios error
   */
  handleError(error) {
    console.error("Customer service API error:", error);

    if (error.response) {
      const status = error.response.status;
      const data = error.response.data;

      if (status === 401) {
        throw new Error(
          "Your session has expired. Please log in again to continue."
        );
      }

      if (status === 403) {
        throw new Error("You don't have permission to perform this action.");
      }

      if (status === 404) {
        throw new Error(data.message || "Resource not found.");
      }

      if (status >= 500) {
        throw new Error(
          "The server is experiencing issues. Please try again later."
        );
      }

      throw new Error(data.message || "An error occurred");
    } else if (error.request) {
      throw new Error(
        "Cannot connect to the server. Please ensure the backend is running."
      );
    } else {
      throw new Error(error.message || "An error occurred");
    }
  }

  /**
   * Get all services assigned to a customer
   * @param {string} customerId - Customer UUID
   * @returns {Promise<Array>} List of assigned services
   */
  async getCustomerServices(customerId) {
    try {
      const response = await apiClient.post("/api/customer-services/list", {
        customerId,
      });

      return response.data.data.services;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Assign a service to a customer
   * @param {string} customerId - Customer UUID
   * @param {string} serviceId - ServiceCatalog UUID
   * @param {string} initialVersionId - ServiceVersion UUID
   * @returns {Promise<Object>} Created CustomerService
   */
  async assignService(customerId, serviceId, initialVersionId) {
    try {
      const response = await apiClient.post("/api/customer-services/assign", {
        customerId,
        serviceId,
        initialVersionId,
      });

      return response.data.data.customer;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Remove a service from a customer
   * @param {string} customerId - Customer UUID
   * @param {string} serviceId - ServiceCatalog UUID
   * @returns {Promise<void>}
   */
  async removeService(customerId, serviceId) {
    try {
      const response = await apiClient.post("/api/customer-services/remove", {
        customerId,
        serviceId,
      });

      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Check if a service is assigned to a customer
   * @param {string} customerId - Customer UUID
   * @param {string} serviceId - ServiceCatalog UUID
   * @returns {Promise<Object>} { isAssigned: boolean, customerServiceId?: string }
   */
  async isServiceAssigned(customerId, serviceId) {
    try {
      const response = await apiClient.post(
        "/api/customer-services/check-assigned",
        {
          customerId,
          serviceId,
        }
      );

      return response.data.data.isAssigned;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Bulk assign multiple services to a customer
   * @param {string} customerId - Customer UUID
   * @param {Array<{serviceId: string, initialVersionId: string}>} services - Services to assign
   * @returns {Promise<Object>} Bulk assignment result with summary
   */
  async bulkAssignServices(customerId, services) {
    try {
      const response = await apiClient.post(
        "/api/customer-services/bulk-assign",
        {
          customerId,
          services,
        }
      );

      return response.data.data.assignment;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Update the current version of a customer service
   * @param {string} customerServiceId - CustomerService UUID
   * @param {string} newVersionId - ServiceVersion UUID
   * @returns {Promise<Object>} Updated CustomerService
   */
  async updateVersion(customerServiceId, newVersionId) {
    try {
      const response = await apiClient.post(
        "/api/customer-services/update-version",
        {
          customerServiceId,
          newVersionId,
        }
      );

      return response.data.data.service;
    } catch (error) {
      this.handleError(error);
    }
  }
}

// Export a singleton instance
const customerServiceService = new CustomerServiceService();
export default customerServiceService;
